import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Camera,
  CheckCircle2,
  Clock,
  MapPin,
  Tag,
  User,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { SitePhotoCapture } from "@/components/shared/SitePhotoCapture";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useTradeCategories } from "@/hooks/use-config";
import {
  type PunchItem,
  type PunchItemPriority,
  type PunchItemStatus,
  usePunchAssignableCompanies,
  usePunchItem,
  useUpdatePunchItem,
  useVerifyPunchItem,
  type VerifyPunchItemInput,
} from "@/hooks/use-punch-list";
import { useSitePhotos, useUploadSitePhoto } from "@/hooks/use-site-photos";
import { useSession } from "@/lib/auth-client";

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  ready_for_review: "Ready for Review",
  verified: "Verified",
  closed: "Closed",
  rejected: "Rejected",
  void: "Void",
};

function statusVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "open":
      return "default";
    case "in_progress":
      return "outline";
    case "ready_for_review":
      return "secondary";
    case "verified":
      return "secondary";
    case "closed":
      return "secondary";
    case "rejected":
      return "destructive";
    case "void":
      return "outline";
    default:
      return "outline";
  }
}

function priorityVariant(
  priority: string | null,
): "default" | "secondary" | "destructive" | "outline" {
  switch (priority) {
    case "critical":
      return "destructive";
    case "high":
      return "destructive";
    case "medium":
      return "default";
    case "low":
      return "secondary";
    default:
      return "outline";
  }
}

function formatPriorityLabel(priority: string | null): string {
  if (!priority) return "—";
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

function formatDate(date: string | null): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

type VerifyAction = "accept" | "reject" | "reassign";

const VERIFY_EDIT_STATUSES: PunchItemStatus[] = [
  "open",
  "in_progress",
  "ready_for_review",
  "verified",
  "closed",
  "rejected",
  "void",
];

interface VerifyDialogProps {
  itemId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function VerifyDialog({ itemId, projectId, open, onOpenChange }: VerifyDialogProps) {
  const [action, setAction] = useState<VerifyAction | null>(null);
  const [note, setNote] = useState("");
  const [reassignCompanyId, setReassignCompanyId] = useState("");

  const verify = useVerifyPunchItem();
  const { options: reassignOptions, isLoading: reassignOptionsLoading } =
    usePunchAssignableCompanies(projectId);

  function handleSubmit() {
    if (!action) return;

    const input: VerifyPunchItemInput = { id: itemId, action, note: note.trim() || undefined };
    if (action === "reassign") {
      input.reassignCompanyId = reassignCompanyId;
    }

    verify.mutate(input, {
      onSuccess: () => {
        setAction(null);
        setNote("");
        setReassignCompanyId("");
        onOpenChange(false);
      },
    });
  }

  function handleClose() {
    if (verify.isPending) return;
    setAction(null);
    setNote("");
    setReassignCompanyId("");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Verify Punch Item</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors min-h-[90px] ${
                action === "accept"
                  ? "border-green-500 bg-green-50 dark:bg-green-950"
                  : "border-border hover:border-green-400"
              }`}
              onClick={() => setAction("accept")}
            >
              <CheckCircle2 className="h-7 w-7 text-green-600" />
              <span className="text-sm font-medium">Accept</span>
              <span className="text-xs text-muted-foreground text-center">Close item</span>
            </button>

            <button
              type="button"
              className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors min-h-[90px] ${
                action === "reject"
                  ? "border-red-500 bg-red-50 dark:bg-red-950"
                  : "border-border hover:border-red-400"
              }`}
              onClick={() => setAction("reject")}
            >
              <XCircle className="h-7 w-7 text-red-600" />
              <span className="text-sm font-medium">Reject</span>
              <span className="text-xs text-muted-foreground text-center">Return for rework</span>
            </button>

            <button
              type="button"
              className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors min-h-[90px] ${
                action === "reassign"
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                  : "border-border hover:border-blue-400"
              }`}
              onClick={() => setAction("reassign")}
            >
              <User className="h-7 w-7 text-blue-600" />
              <span className="text-sm font-medium">Re-assign</span>
              <span className="text-xs text-muted-foreground text-center">Change assignee</span>
            </button>
          </div>

          {action !== null && (
            <div className="space-y-2">
              <Label htmlFor="verify-note">Note</Label>
              <Textarea
                id="verify-note"
                placeholder={
                  action === "accept"
                    ? "Optional acceptance note"
                    : action === "reject"
                      ? "Describe what needs to be corrected"
                      : "Reason for re-assignment"
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </div>
          )}

          {action === "reassign" && (
            <div className="space-y-2">
              <Label htmlFor="verify-reassign-co">Assign to company</Label>
              <Select
                value={reassignCompanyId || undefined}
                onValueChange={setReassignCompanyId}
                disabled={reassignOptionsLoading}
              >
                <SelectTrigger id="verify-reassign-co" className="min-h-[44px]">
                  <SelectValue
                    placeholder={
                      reassignOptionsLoading ? "Loading companies…" : "Select subcontractor"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {reassignOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            onClick={handleClose}
            disabled={verify.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="min-h-[44px]"
            disabled={
              !action || verify.isPending || (action === "reassign" && !reassignCompanyId.trim())
            }
            onClick={handleSubmit}
          >
            {verify.isPending ? "Saving…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditPunchItemDialogProps {
  item: PunchItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isInternal: boolean;
}

function EditPunchItemDialog({ item, open, onOpenChange, isInternal }: EditPunchItemDialogProps) {
  const update = useUpdatePunchItem();
  const [description, setDescription] = useState(item.description);
  const [location, setLocation] = useState(item.location ?? "");
  const [roomOrArea, setRoomOrArea] = useState(item.roomOrArea ?? "");
  const [trade, setTrade] = useState(item.trade ?? "");
  const [priority, setPriority] = useState<PunchItemPriority>(item.priority);
  const [dueDate, setDueDate] = useState(item.dueDate ?? "");
  const [status, setStatus] = useState<PunchItemStatus>(item.status);
  const [assignedCompanyId, setAssignedCompanyId] = useState(item.assignedCompanyId ?? "");

  const { options: assigneeOptions, isLoading: assigneesLoading } = usePunchAssignableCompanies(
    item.projectId,
  );

  const { data: tradeCategories = [], isLoading: tradesLoading } = useTradeCategories();
  const activeTrades = useMemo(
    () => tradeCategories.filter((t) => t.isActive !== false),
    [tradeCategories],
  );
  const tradeNameOptions = useMemo(() => {
    const names = activeTrades.map((t) => t.name.trim()).filter(Boolean);
    const trimmed = trade.trim();
    if (!trimmed) return names;
    const known = names.some((n) => n.toLowerCase() === trimmed.toLowerCase());
    if (known) return names;
    return [...names, trimmed];
  }, [activeTrades, trade]);

  useEffect(() => {
    if (!open) return;
    setDescription(item.description);
    setLocation(item.location ?? "");
    setRoomOrArea(item.roomOrArea ?? "");
    setTrade(item.trade ?? "");
    setPriority(item.priority);
    setDueDate(item.dueDate ?? "");
    setStatus(item.status);
    setAssignedCompanyId(item.assignedCompanyId ?? "");
  }, [open, item]);

  function handleDialogOpenChange(next: boolean) {
    if (!next && update.isPending) return;
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const base = {
      id: item.id,
      description: description.trim(),
      location: location.trim() === "" ? null : location.trim(),
      roomOrArea: roomOrArea.trim() === "" ? null : roomOrArea.trim(),
      trade: trade.trim() === "" ? null : trade.trim(),
      priority,
      dueDate: dueDate.trim() === "" ? null : dueDate.trim(),
    };
    await update.mutateAsync({
      ...base,
      ...(isInternal
        ? {
            status,
            assignedCompanyId: assignedCompanyId.trim() === "" ? null : assignedCompanyId.trim(),
          }
        : {}),
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Punch Item</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-punch-desc">Description</Label>
              <Textarea
                id="edit-punch-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                required
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-punch-location">Location</Label>
              <Input
                id="edit-punch-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="min-h-[44px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-punch-room">Room / area</Label>
              <Input
                id="edit-punch-room"
                value={roomOrArea}
                onChange={(e) => setRoomOrArea(e.target.value)}
                className="min-h-[44px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-punch-trade">Trade</Label>
              <Select
                value={trade.trim() === "" ? "__none__" : trade}
                onValueChange={(v) => setTrade(v === "__none__" ? "" : v)}
                disabled={tradesLoading}
              >
                <SelectTrigger id="edit-punch-trade" className="min-h-[44px]">
                  <SelectValue placeholder={tradesLoading ? "Loading…" : "Select trade"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {tradeNameOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!tradesLoading && activeTrades.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add trade categories under Admin → Configuration → Trade categories.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as PunchItemPriority)}>
                  <SelectTrigger className="min-h-[44px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-punch-due">Due date</Label>
                <Input
                  id="edit-punch-due"
                  type="date"
                  value={dueDate && dueDate.length >= 10 ? dueDate.slice(0, 10) : dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="min-h-[44px]"
                />
              </div>
            </div>

            {isInternal && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as PunchItemStatus)}>
                    <SelectTrigger className="min-h-[44px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VERIFY_EDIT_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_LABELS[s] ?? s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-punch-assignee">Responsible company</Label>
                  <Select
                    value={assignedCompanyId || "__none__"}
                    onValueChange={(v) => setAssignedCompanyId(v === "__none__" ? "" : v)}
                    disabled={assigneesLoading}
                  >
                    <SelectTrigger id="edit-punch-assignee" className="min-h-[44px]">
                      <SelectValue placeholder={assigneesLoading ? "Loading…" : "Unassigned"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {assigneeOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              onClick={() => handleDialogOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-[44px]" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PunchListDetailPage() {
  const rawParams = useParams({ strict: false });
  const id = typeof rawParams.id === "string" ? rawParams.id : "";

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const { data: session } = useSession();
  const { data: item, isLoading, isError, error, refetch } = usePunchItem(id, id !== "");

  const { data: punchPhotos = [], isLoading: photosLoading } = useSitePhotos(
    item ? { projectId: item.projectId, punchListItemId: item.id } : undefined,
  );
  const uploadPhoto = useUploadSitePhoto();
  const advanceWorkflow = useUpdatePunchItem();

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <Link
          to="/punch-list"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Punch List
        </Link>
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load punch item"}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <Link
          to="/punch-list"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Punch List
        </Link>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Punch item not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const canVerify = item.status === "ready_for_review";
  const role = session?.user?.role;
  const isInternal = role !== "contractor";
  // Better Auth's typed user strips custom fields; companyId is added by
  // our session adapter (see src/lib/auth.ts) and is present at runtime.
  const userCompanyId =
    (session?.user as { companyId?: string | null } | undefined)?.companyId ?? null;
  const isAssignedSub =
    role === "contractor" &&
    Boolean(userCompanyId && item.assignedCompanyId === userCompanyId) &&
    ["open", "in_progress", "rejected"].includes(item.status);
  const canEdit =
    item.status !== "void" &&
    (role !== "contractor" || ["open", "in_progress", "rejected"].includes(item.status));

  const photosReadOnly = item.status === "void";

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/punch-list"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Punch List
        </Link>

        {canEdit && (
          <Button variant="outline" className="min-h-[44px]" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
        )}
      </div>

      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {item.punchNumber !== null && (
              <span className="text-sm text-muted-foreground font-mono">#{item.punchNumber}</span>
            )}
            <Badge variant={statusVariant(item.status)}>
              {item.status ? (STATUS_LABELS[item.status] ?? item.status) : "—"}
            </Badge>
            <Badge variant={priorityVariant(item.priority)}>
              {formatPriorityLabel(item.priority)} Priority
            </Badge>
          </div>
          <h1 className="text-xl font-bold">{item.description ?? "No description"}</h1>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Project</p>
                <p className="font-medium">{item.projectName ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Location</p>
                <p className="font-medium">{item.location ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Room / area</p>
                <p className="font-medium">{item.roomOrArea ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Assigned To</p>
                <p className="font-medium">{item.assignedCompanyName ?? item.assignedTo ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <CalendarDays className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Due Date</p>
                <p className="font-medium">{formatDate(item.dueDate)}</p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-muted-foreground text-xs">Created</p>
                <p className="font-medium">{formatDate(item.createdDate)}</p>
              </div>
            </div>

            {item.trade && (
              <div className="flex items-start gap-2">
                <Tag className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs">Trade</p>
                  <p className="font-medium">{item.trade}</p>
                </div>
              </div>
            )}

            {item.completedDate && (
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs">Completed</p>
                  <p className="font-medium">{formatDate(item.completedDate)}</p>
                </div>
              </div>
            )}
          </div>

          {item.verificationNote && (
            <div className="pt-2 border-t text-sm">
              <p className="text-muted-foreground text-xs mb-1">Verification Note</p>
              <p>{item.verificationNote}</p>
            </div>
          )}

          {item.verifiedAt && (
            <div className="text-sm">
              <p className="text-muted-foreground text-xs mb-1">Verified At</p>
              <p>{formatDate(item.verifiedAt)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {isAssignedSub && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Field workflow</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {(item.status === "open" || item.status === "rejected") && (
              <Button
                type="button"
                variant="secondary"
                className="min-h-[44px]"
                disabled={advanceWorkflow.isPending}
                onClick={() => advanceWorkflow.mutate({ id: item.id, status: "in_progress" })}
              >
                Mark in progress
              </Button>
            )}
            <Button
              type="button"
              className="min-h-[44px]"
              disabled={advanceWorkflow.isPending}
              onClick={() => advanceWorkflow.mutate({ id: item.id, status: "ready_for_review" })}
            >
              Submit for review
            </Button>
          </CardContent>
        </Card>
      )}

      {canVerify && (
        <Card className="border-amber-200 dark:border-amber-800">
          <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-medium text-sm">Ready for Verification</p>
              <p className="text-xs text-muted-foreground">
                Accept to close the item or reject to return it for rework.
              </p>
            </div>
            <Button className="min-h-[44px]" onClick={() => setVerifyOpen(true)}>
              Verify Item
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Photos
            </CardTitle>
            {!photosReadOnly && (
              <SitePhotoCapture
                disabled={uploadPhoto.isPending}
                onCapture={(file, location) => {
                  void uploadPhoto.mutateAsync({
                    file,
                    projectId: item.projectId,
                    punchListItemId: item.id,
                    category: "issue",
                    lat: location?.lat,
                    lng: location?.lng,
                  });
                }}
                onMultiCapture={(files, location) => {
                  void Promise.all(
                    files.map((f) =>
                      uploadPhoto.mutateAsync({
                        file: f,
                        projectId: item.projectId,
                        punchListItemId: item.id,
                        category: "issue",
                        lat: location?.lat,
                        lng: location?.lng,
                      }),
                    ),
                  );
                }}
                multiple
                label={uploadPhoto.isPending ? "Uploading…" : "Add Photo"}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {photosLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading photos…</p>
          ) : punchPhotos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
              <Camera className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm text-center max-w-sm px-4">
                {photosReadOnly
                  ? "No photos on this item."
                  : "No photos yet. Use Add Photo to attach verification or issue images."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {punchPhotos.map((photo) => (
                <a
                  key={photo.id}
                  href={photo.fullUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square rounded-lg overflow-hidden border bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <img
                    src={photo.thumbnailUrl}
                    alt={photo.caption ?? "Punch item photo"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground py-4 text-center">No activity yet.</p>
        </CardContent>
      </Card>

      <VerifyDialog
        itemId={id}
        projectId={item.projectId}
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
      />
      <EditPunchItemDialog
        item={item}
        open={editOpen}
        onOpenChange={setEditOpen}
        isInternal={isInternal}
      />
    </div>
  );
}
